/**
 * La salle d'audience en eau-forte coloriée (images générées par art/build.py).
 *
 * - TrialAvatar : portrait gravé en médaillon + états pilotés par React :
 *   `speaking` (halo pulsé), `thinking` (anneau en rotation, l'IA rédige),
 *   `strikeKey` (onde de choc du marteau).
 * - CourtSprite : un personnage en buste, image par expression ; la bouche
 *   s'anime quand il parle.
 * - CourtDiorama : la salle en calques (mur, parquet, estrade, tables,
 *   public) dans un conteneur en perspective. Parallaxe à la souris ; la
 *   « caméra » se rapproche de l'intervenant mis en avant ; un faisceau tombe
 *   sur celui qui a la parole.
 *
 * Cinq salles (CourtKey) : la cour française et ses déclinaisons gravées
 * (art/courts/build_courts.py). La salle courante vient de CourtContext ;
 * une image absente d'une déclinaison retombe sur celle de la cour française.
 *
 * Tout mouvement est coupé si l'utilisateur demande moins d'animations.
 */
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { createContext, useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

export type StageRole = 'judge' | 'clerk' | 'prosecution' | 'defense' | 'witness';
export type Expression = 'idle' | 'talk' | 'angry' | 'strike' | 'shocked' | 'smug' | 'reading';

// Images gravées générées par art/build.py, empaquetées par Vite (noms hachés).
const IMAGES = import.meta.glob('./art/img/*.webp', { eager: true, import: 'default' }) as Record<string, string>;
const art = (name: string, court: CourtKey = 'fr') =>
  (court !== 'fr' && IMAGES[`./art/img/court_${court}_${name}.webp`]) || IMAGES[`./art/img/court_${name}.webp`] || '';

/** Les salles : la cour française et ses déclinaisons. */
export type CourtKey = 'fr' | 'us' | 'uk' | 'gr' | 'eu';
export const COURTS: CourtKey[] = ['fr', 'us', 'uk', 'gr', 'eu'];
/** Salle de l'affaire en cours (la cour française par défaut). */
export const CourtContext = createContext<CourtKey>('fr');
export const useCourt = () => useContext(CourtContext);

/**
 * Salle d'une affaire : un grand procès se joue dans la salle de son époque,
 * une affaire du marché européen devant la Cour de justice de l'Union.
 */
export function courtOf(trial?: { jurisdiction?: string | null; generated_case?: { famous?: { court?: string } | null } | null } | null): CourtKey {
  const famous = trial?.generated_case?.famous;
  if (famous) return (COURTS as string[]).includes(famous.court ?? '') ? (famous.court as CourtKey) : 'us';
  return trial?.jurisdiction === 'eu' ? 'eu' : 'fr';
}

/** Expressions disponibles par personnage (cf. art/expr.py). */
const EXPRESSIONS: Record<StageRole, Expression[]> = {
  judge: ['idle', 'talk', 'angry', 'strike'],
  clerk: ['idle', 'talk', 'reading'],
  witness: ['idle', 'talk', 'shocked'],
  prosecution: ['idle', 'talk', 'angry'],
  defense: ['idle', 'talk', 'smug'],
};

export const ROLE_AVATARS: Record<string, string> = {
  judge: art('portrait_judge'),
  clerk: art('portrait_clerk'),
  prosecution: art('portrait_prosecution'),
  defense: art('portrait_defense'),
  witness: art('portrait_witness'),
};

/** Couleur d'accent par rôle (halo, faisceau, mise en avant). */
export const ROLE_COLORS: Record<string, string> = {
  judge: '#c08a32',
  clerk: '#8aa3b8',
  prosecution: '#b5523b',
  defense: '#3d5a73',
  witness: '#7fa595',
  system: '#a0907a',
};

/** Variantes de témoin : cochon (''), lièvre ('2'), humain en sueur ('3'). */
export type WitnessVariant = '' | '2' | '3';
const WITNESS_VARIANTS: WitnessVariant[] = ['', '2', '3'];

/**
 * Variante stable d'un témoin (même tête partout : dossier, procès-verbal,
 * barre). Selon son rang dans le dossier quand on le connaît — la rotation
 * garantit des têtes variées —, sinon d'après un hachage de son identifiant.
 */
export function witnessVariant(id?: string | null, order?: { id: string }[]): WitnessVariant {
  if (!id) return '';
  const rank = order?.findIndex(w => w.id === id) ?? -1;
  if (rank >= 0) return WITNESS_VARIANTS[rank % WITNESS_VARIANTS.length];
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
  return WITNESS_VARIANTS[h % WITNESS_VARIANTS.length];
}

export function frameUrl(role: StageRole, expr: Expression, variant: WitnessVariant = '', court: CourtKey = 'fr'): string {
  // Les variantes de témoin n'existent que dans la cour française
  if (court === 'fr' && role === 'witness' && variant) return art(`witness${variant}_${expr === 'idle' ? 'idle' : 'talk'}`);
  const e = EXPRESSIONS[role].includes(expr) ? expr : expr === 'idle' ? 'idle' : 'talk';
  return art(`${role}_${e}`, court);
}

/** Portrait gravé d'un rôle (et de la variante de témoin), dans une salle. */
export function portraitUrl(role: string, variant: WitnessVariant = '', court: CourtKey = 'fr'): string {
  if (court !== 'fr') return art(`portrait_${role}`, court) || ROLE_AVATARS[role];
  return role === 'witness' && variant ? art(`portrait_witness${variant}`) : ROLE_AVATARS[role];
}

/** Portraits de la salle courante (CourtContext). */
export function usePortrait() {
  const court = useCourt();
  return (role: string, variant: WitnessVariant = '') => portraitUrl(role, variant, court);
}

/** Bouche qui s'ouvre et se ferme tant que `active` (une image sur deux). */
function useMouthFlap(active: boolean, ms = 170) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!active || reduce) { setOpen(true); return; }
    const id = setInterval(() => setOpen(o => !o), ms);
    return () => clearInterval(id);
  }, [active, reduce, ms]);
  return open;
}

/** Précharge toutes les images de la distribution (pas de clignotement au premier changement). */
function usePreloadArt(court: CourtKey) {
  useEffect(() => {
    (Object.keys(EXPRESSIONS) as StageRole[]).forEach(r =>
      EXPRESSIONS[r].forEach(e => { const i = new Image(); i.src = frameUrl(r, e, '', court); }));
    if (court === 'fr')
      (['2', '3'] as WitnessVariant[]).forEach(v =>
        (['idle', 'talk'] as Expression[]).forEach(e => { const i = new Image(); i.src = frameUrl('witness', e, v); }));
  }, [court]);
}

export function TrialAvatar({
  role,
  size = 44,
  speaking = false,
  thinking = false,
  strikeKey,
  className = '',
  alt,
  variant = '',
}: {
  role: string;
  size?: number;
  /** Variante de témoin (cf. witnessVariant). */
  variant?: WitnessVariant;
  speaking?: boolean;
  thinking?: boolean;
  /** Change de valeur → coup de marteau (onde de choc). */
  strikeKey?: string | number | null;
  className?: string;
  alt?: string;
}) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const color = ROLE_COLORS[role] ?? ROLE_COLORS.system;
  const src = portraitUrl(role, variant, useCourt());
  const [strike, setStrike] = useState(0);
  // La décision déjà présente au montage est de l'historique : pas de coup de marteau
  const mountedKey = useRef(strikeKey);

  useEffect(() => {
    if (strikeKey == null || strikeKey === mountedKey.current) return;
    mountedKey.current = null;
    setStrike(s => s + 1);
  }, [strikeKey]);

  const label = alt ?? t(`tribunal.roles.${role}`, role);
  const animated = !reduce;

  return (
    <div className={`relative flex-shrink-0 ${className}`} style={{ width: size, height: size }}>
      {speaking && animated && (
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full"
          style={{ background: color }}
          initial={{ opacity: 0.35, scale: 1 }}
          animate={{ opacity: [0.35, 0, 0.35], scale: [1, 1.45, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      {thinking && (
        <motion.span
          aria-hidden="true"
          className="absolute -inset-1 rounded-full border-2 border-dashed"
          style={{ borderColor: color }}
          animate={animated ? { rotate: 360 } : undefined}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      )}
      <AnimatePresence>
        {strike > 0 && animated && (
          <motion.span
            key={strike}
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-4"
            style={{ borderColor: color }}
            initial={{ opacity: 0.9, scale: 0.9 }}
            animate={{ opacity: 0, scale: 2.4 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>
      <div
        className="relative w-full h-full rounded-full overflow-hidden"
        style={{ background: '#f3e8cc', boxShadow: speaking ? `0 0 0 2px ${color}` : '0 0 0 1px rgba(0,0,0,.08)' }}
      >
        {src ? (
          <img src={src} alt={label} draggable={false} className="w-full h-full object-cover select-none" />
        ) : (
          <div role="img" aria-label={label} className="w-full h-full bg-muted" />
        )}
      </div>
    </div>
  );
}

/** Personnage gravé en buste ; `talking` anime la bouche. */
export function CourtSprite({
  role, expression = 'idle', talking = false, style, className = '', alt = '', variant = '',
}: {
  role: StageRole;
  variant?: WitnessVariant;
  expression?: Expression;
  talking?: boolean;
  style?: CSSProperties;
  className?: string;
  alt?: string;
}) {
  const court = useCourt();
  const open = useMouthFlap(talking && (expression === 'talk' || expression === 'idle'));
  const expr: Expression = talking && (expression === 'idle' || expression === 'talk') ? (open ? 'talk' : 'idle') : expression;
  return <img src={frameUrl(role, expr, variant, court)} alt={alt} draggable={false} className={`select-none ${className}`} style={style} />;
}

// ── Le diorama ───────────────────────────────────────────────────────────────

/** Scène de référence : 1440 × 756 ; centre optique propre à chaque salle (le juge). */
const STAGE_W = 1440;
const STAGE_H = 756;

type Seat = { x: number; top: number; w: number; layer: 'court' | 'bar' };
const COL: Record<StageRole, string> = {
  judge: '217,164,65', clerk: '138,163,184', witness: '127,165,149', prosecution: '181,82,59', defense: '138,163,184',
};
/**
 * Personnages : centre x, haut du buste, largeur (repère 200 × 220), calque
 * (« court » : derrière le banc ; « bar » : derrière les tables). Les salles
 * hors France reprennent les places de art/courts/court_*.py.
 */
const STAGES: Record<CourtKey, { origin: { x: number; y: number }; lift?: number; seats: Record<StageRole, Seat> }> = {
  fr: { origin: { x: 906, y: 190 }, seats: {
    judge: { x: 906, top: 8, w: 168, layer: 'court' }, clerk: { x: 572, top: 102, w: 104, layer: 'court' },
    witness: { x: 1281, top: 40, w: 118, layer: 'court' }, prosecution: { x: 480, top: 176, w: 180, layer: 'bar' },
    defense: { x: 1332, top: 176, w: 180, layer: 'bar' } } },
  us: { origin: { x: 760, y: 190 }, lift: 170, seats: {
    judge: { x: 760, top: 168, w: 214, layer: 'court' }, witness: { x: 1146, top: 230, w: 150, layer: 'court' },
    clerk: { x: 760, top: 446, w: 110, layer: 'bar' }, prosecution: { x: 400, top: 426, w: 190, layer: 'bar' },
    defense: { x: 980, top: 426, w: 190, layer: 'bar' } } },
  uk: { origin: { x: 760, y: 190 }, lift: 180, seats: {
    judge: { x: 760, top: 170, w: 230, layer: 'court' }, witness: { x: 446, top: 262, w: 150, layer: 'court' },
    clerk: { x: 760, top: 470, w: 130, layer: 'bar' }, prosecution: { x: 470, top: 450, w: 200, layer: 'bar' },
    defense: { x: 1030, top: 450, w: 200, layer: 'bar' } } },
  gr: { origin: { x: 720, y: 190 }, lift: 150, seats: {
    judge: { x: 720, top: 170, w: 170, layer: 'court' }, prosecution: { x: 640, top: 290, w: 200, layer: 'bar' },
    defense: { x: 860, top: 292, w: 200, layer: 'bar' }, witness: { x: 440, top: 330, w: 170, layer: 'bar' },
    clerk: { x: 1250, top: 340, w: 150, layer: 'bar' } } },
  eu: { origin: { x: 720, y: 190 }, lift: 165, seats: {
    judge: { x: 720, top: 150, w: 176, layer: 'court' }, clerk: { x: 1310, top: 400, w: 150, layer: 'bar' },
    witness: { x: 720, top: 470, w: 150, layer: 'bar' }, prosecution: { x: 520, top: 430, w: 210, layer: 'bar' },
    defense: { x: 920, top: 430, w: 210, layer: 'bar' } } },
};
const ROLES: StageRole[] = ['judge', 'clerk', 'witness', 'prosecution', 'defense'];
// lift : la scène est remontée d'autant (px du repère) pour que le juge tombe à la hauteur du Président français.
/** Profondeur des calques (translateZ, compensation d'échelle). */
const LAYERS = {
  wall:   { z: -240, s: 1.2 },
  floor:  { z: -60,  s: 1.05 },
  court:  { z: -110, s: 1.09 },
  bar:    { z: 0,    s: 1 },
  public: { z: 40,   s: 0.964 },
};

/** Position d'un visage à l'écran (repère scène), compte tenu de l'échelle de son calque. */
function faceOf(court: CourtKey, r: StageRole) {
  const { origin, seats } = STAGES[court];
  const sp = seats[r];
  const s = LAYERS[sp.layer].s;
  return { x: origin.x + (sp.x - origin.x) * s, y: origin.y + (sp.top + sp.w * 0.45 - origin.y) * s };
}

function spriteBox(court: CourtKey, r: StageRole): CSSProperties {
  const { x, top, w } = STAGES[court].seats[r];
  // Les images couvrent le repère -20 -50 240 280 (débords : marteau, mains)
  return { position: 'absolute', left: x - w / 2 - w * 0.1, top: top - w * 0.25, width: w * 1.2, height: w * 1.4 };
}

function EmptySeat({ role, court }: { role: 'prosecution' | 'defense'; court: CourtKey }) {
  const { x, top, w } = STAGES[court].seats[role];
  return (
    <svg viewBox="0 0 200 220" aria-hidden="true" style={{ position: 'absolute', left: x - w / 2, top, width: w, height: w * 1.1, overflow: 'visible' }}>
      <g fill="none" stroke="#5a4632" strokeWidth={2.4} strokeDasharray="7 6" opacity={0.75}>
        <path d="M16 222 C20 168 50 140 100 134 C150 140 180 168 184 222" />
        <ellipse cx="100" cy="86" rx="34" ry="40" />
      </g>
    </svg>
  );
}

export function CourtDiorama({
  court: courtProp,
  anchor,
  fullStage = false,
  focus = null,
  currentSpeaker = null,
  spotlight = null,
  talking = null,
  expressions = {},
  emptySeats = [],
  showWitness = true,
  witness = '',
  judgeStrikeKey,
  parallax = true,
  span = 1100,
  className = '',
}: {
  /** Salle (sinon celle de CourtContext). */
  court?: CourtKey;
  /** Fraction de la largeur où placer le juge (sinon centré, sans vide sur les bords). */
  anchor?: number;
  /** Salle entière, sans remonter la scène sur le juge (hero de l'accueil). */
  fullStage?: boolean;
  /** Intervenant mis en avant : la caméra s'en approche, les autres s'estompent. */
  focus?: StageRole | null;
  /** Avocat qui a la parole (faisceau doux). */
  currentSpeaker?: 'prosecution' | 'defense' | null;
  /** Faisceau sur un intervenant, sans rapprocher la caméra. */
  spotlight?: StageRole | null;
  /** Personnage dont la bouche s'anime. */
  talking?: StageRole | null;
  expressions?: Partial<Record<StageRole, Expression>>;
  emptySeats?: ('prosecution' | 'defense')[];
  showWitness?: boolean;
  /** Variante du témoin à la barre. */
  witness?: WitnessVariant;
  /** Change de valeur → coup de marteau du Président. */
  judgeStrikeKey?: string | null;
  parallax?: boolean;
  /** Largeur de scène (px du repère) à faire tenir dans le conteneur. */
  span?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ctxCourt = useCourt();
  const court = courtProp ?? ctxCourt;
  const stage = STAGES[court];
  const { origin: ORIGIN, seats: SEATS } = stage;
  const lift = fullStage ? 0 : stage.lift ?? 0;
  usePreloadArt(court);
  const boxRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [striking, setStriking] = useState(false);
  const mountedStrike = useRef(judgeStrikeKey);
  const rafRef = useRef(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Coup de marteau : image « strike » un court instant
  useEffect(() => {
    if (judgeStrikeKey == null || judgeStrikeKey === mountedStrike.current) return;
    mountedStrike.current = null;
    setStriking(true);
    const id = setTimeout(() => setStriking(false), 650);
    return () => clearTimeout(id);
  }, [judgeStrikeKey]);

  // Parallaxe : au plus une mise à jour par image, suivie sur toute la zone parente
  useEffect(() => {
    const el = boxRef.current?.parentElement;
    if (!el || !parallax || reduce) return;
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; setTilt({ x, y }); });
    };
    el.addEventListener('pointermove', onMove);
    return () => { el.removeEventListener('pointermove', onMove); cancelAnimationFrame(rafRef.current); };
  }, [parallax, reduce]);

  // La scène couvre toujours le conteneur ; on centre sur l'estrade sans laisser de vide sur les bords
  const scale = size.w ? Math.max(size.w / span, size.h / (STAGE_H - lift), size.w / STAGE_W) : 1;
  const offsetX = anchor != null
    ? Math.max(size.w - STAGE_W * scale, anchor * size.w - ORIGIN.x * scale)
    : Math.min(0, Math.max(size.w - STAGE_W * scale, size.w / 2 - ORIGIN.x * scale));
  const lit: StageRole | null = focus ?? spotlight ?? currentSpeaker;
  const fp = faceOf(court, lit ?? 'judge');
  const fc = COL[lit ?? 'judge'];

  const layerStyle = (k: keyof typeof LAYERS): CSSProperties => ({
    position: 'absolute', inset: 0, transformOrigin: `${ORIGIN.x}px ${ORIGIN.y}px`, transformStyle: 'preserve-3d',
    transform: `translateZ(${LAYERS[k].z}px) scale(${LAYERS[k].s})`,
  });
  const img = (name: string) => {
    const src = court !== 'fr' && name === 'light' ? art('layer_light_plain') : art(`layer_${name}`, court);
    return src ? <img src={src} alt="" draggable={false} className="absolute inset-0 w-full h-full select-none" /> : null;
  };
  const sprite = (r: StageRole) => {
    if ((r === 'prosecution' || r === 'defense') && emptySeats.includes(r)) return <EmptySeat key={r} role={r} court={court} />;
    if (r === 'witness' && !showWitness) return null;
    const on = lit === r;
    const dim = !!focus && !on;
    const expr: Expression = r === 'judge' && striking ? 'strike' : expressions[r] ?? 'idle';
    return (
      <CourtSprite
        key={r}
        role={r}
        expression={expr}
        talking={talking === r}
        variant={r === 'witness' ? witness : ''}
        style={{
          ...spriteBox(court, r),
          transition: 'filter .5s',
          filter: on
            ? `drop-shadow(0 0 16px rgba(${COL[r]},.75)) drop-shadow(0 12px 14px rgba(0,0,0,.4))`
            : `drop-shadow(0 12px 14px rgba(0,0,0,.4)) brightness(${dim ? 0.5 : 0.95})`,
        }}
      />
    );
  };
  const pool = (r: StageRole) => {
    const { x, top, w } = SEATS[r];
    const col = COL[r];
    const on = lit === r;
    return (
      <div
        key={`pool-${r}`}
        style={{
          position: 'absolute', left: x - w * 0.75, top: top + w * 0.88, width: w * 1.5, height: w * 0.34, borderRadius: '50%',
          background: `radial-gradient(closest-side, rgba(${col},${on ? (focus ? 0.7 : 0.4) : 0}), rgba(${col},0))`,
          transition: 'background .5s',
        }}
      />
    );
  };
  const rig: CSSProperties = {
    position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transformOrigin: `${Math.round(fp.x)}px ${Math.round(fp.y)}px`,
    transition: reduce ? undefined : 'transform 1.1s cubic-bezier(.22,1,.36,1)',
    transform: `${focus && !reduce ? 'scale(1.14) ' : ''}rotateY(${(tilt.x * -5).toFixed(2)}deg) rotateX(${(tilt.y * 3).toFixed(2)}deg)`,
  };

  return (
    <CourtContext.Provider value={court}>
    <div ref={boxRef} aria-hidden="true" className={`absolute inset-0 overflow-hidden select-none ${className}`} style={{ background: '#efe3c4' }}>
      <div
        style={{
          position: 'absolute', left: offsetX, top: -lift * scale, width: STAGE_W, height: STAGE_H,
          transform: `scale(${scale})`, transformOrigin: '0 0', perspective: 1100, perspectiveOrigin: `${ORIGIN.x}px ${ORIGIN.y}px`,
        }}
      >
        <div style={rig}>
          <div style={layerStyle('wall')}>{img('wall')}</div>
          <div style={layerStyle('floor')}>{img('floor')}</div>
          <div style={layerStyle('court')}>
            {ROLES.filter(r => SEATS[r].layer === 'court').map(r => [pool(r), sprite(r)])}
            {img('bench')}
          </div>
          <div style={layerStyle('bar')}>
            {ROLES.filter(r => SEATS[r].layer === 'bar').map(r => [pool(r), sprite(r)])}
            {img('tables')}
          </div>
          <div style={layerStyle('public')}>{img('public')}</div>
          {/* La salle s'assombrit autour de l'intervenant mis en avant ; faisceau sur celui qui a la parole */}
          <div
            style={{
              position: 'absolute', inset: -80, pointerEvents: 'none', transition: 'opacity .6s', opacity: focus ? 1 : lit ? 0.35 : 0,
              background: `radial-gradient(ellipse 260px 300px at ${Math.round(fp.x + 80)}px ${Math.round(fp.y + 80)}px, rgba(59,42,26,0) 0%, rgba(59,42,26,.55) 100%)`,
            }}
          />
          {lit && (
            <div
              style={{
                position: 'absolute', left: Math.round(fp.x - 150), top: -40, width: 300, height: Math.round(fp.y + 110), pointerEvents: 'none',
                mixBlendMode: 'screen', filter: 'blur(6px)', clipPath: 'polygon(40% 0, 60% 0, 100% 100%, 0 100%)',
                background: `linear-gradient(to bottom, rgba(${fc},.05), rgba(${fc},${focus ? 0.42 : 0.16}))`,
                transition: reduce ? undefined : 'left .9s cubic-bezier(.22,1,.36,1), height .9s',
              }}
            />
          )}
        </div>
        {img('light')}
      </div>
    </div>
    </CourtContext.Provider>
  );
}
